import {
  CUSTOMER_STATUS,
  STRING_FILTER_OPERATOR,
} from '@monorepo/shared/schemas/common'
import { X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import { useDebounce } from '@/shared/hooks/useDebounce'

interface FilterOption {
  value: string
  label: string
}

interface FilterConfig {
  fields: FilterOption[]
  operators: FilterOption[]
  statuses: FilterOption[]
}

interface Filters {
  page?: number
  limit?: number
  field?: string
  operator?: string
  value?: string
  status?: string
}

interface CustomerFiltersProps {
  filters: Filters
  onFiltersChange: (filters: Filters) => void
  onReset: () => void
}

const filterConfig: FilterConfig = {
  fields: [
    { value: 'name', label: 'Customer Name' },
    { value: 'email', label: 'Email' },
    { value: 'external_id', label: 'External ID' },
    { value: 'billing_id', label: 'Billing ID' },
    { value: 'organization_name', label: 'Organization Name' },
  ],
  operators: [
    { value: STRING_FILTER_OPERATOR.is, label: 'Is' },
    { value: STRING_FILTER_OPERATOR.starts_with, label: 'Starts with' },
    { value: STRING_FILTER_OPERATOR.ends_with, label: 'Ends with' },
    { value: STRING_FILTER_OPERATOR.contains, label: 'Contains' },
  ],
  statuses: [
    { value: CUSTOMER_STATUS.active, label: 'Active' },
    { value: CUSTOMER_STATUS.inactive, label: 'Inactive' },
    { value: CUSTOMER_STATUS.suspended, label: 'Suspended' },
    { value: CUSTOMER_STATUS.archived, label: 'Archived' },
  ],
}

export function CustomerFilters({
  filters,
  onFiltersChange,
  onReset,
}: CustomerFiltersProps) {
  // Local state for immediate UI updates
  const [localValue, setLocalValue] = useState(filters.value || '')

  // Refs to access latest props without triggering re-renders
  const filtersRef = useRef(filters)
  const onFiltersChangeRef = useRef(onFiltersChange)
  filtersRef.current = filters
  onFiltersChangeRef.current = onFiltersChange

  // Debounce the search value (500ms delay)
  const debouncedValue = useDebounce(localValue, 500)

  // Sync debounced value to parent
  useEffect(() => {
    if (debouncedValue !== filtersRef.current.value) {
      onFiltersChangeRef.current({
        ...filtersRef.current,
        value: debouncedValue || undefined,
      })
    }
  }, [debouncedValue])

  // Sync local state with props (when filters are reset externally)
  useEffect(() => {
    setLocalValue(filters.value || '')
  }, [filters.value])

  const updateFilter = useCallback(
    (key: keyof Filters, value: string | undefined) => {
      onFiltersChangeRef.current({
        ...filtersRef.current,
        [key]: value,
      })
    },
    []
  )

  const clearFilter = useCallback(
    (key: keyof Filters) => {
      if (key === 'value') {
        setLocalValue('')
      }
      updateFilter(key, undefined)
    },
    [updateFilter]
  )

  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'page' || key === 'limit' || key === 'sort' || key === 'order')
      return false
    return (
      value !== undefined &&
      value !== '' &&
      (Array.isArray(value) ? value.length > 0 : true)
    )
  })

  return (
    <div className='mb-6'>
      {/* Single Row Filter - No Card Background */}
      <div className='flex gap-3 items-end justify-between'>
        <div className='flex gap-3 items-center'>
          {/* Field Select - 25% larger (w-32 -> w-40) */}
          <div className='w-40'>
            <Select
              value={filters.field || ''}
              onValueChange={value => updateFilter('field', value)}
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Select field' />
              </SelectTrigger>
              <SelectContent>
                {filterConfig.fields.map(field => (
                  <SelectItem key={field.value} value={field.value}>
                    {field.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Operator Select - Small */}
          <div className='w-28'>
            <Select
              value={filters.operator || ''}
              onValueChange={value => updateFilter('operator', value)}
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Operator' />
              </SelectTrigger>
              <SelectContent>
                {filterConfig.operators.map(operator => (
                  <SelectItem key={operator.value} value={operator.value}>
                    {operator.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Value Input - 25% larger (w-64 -> w-80) */}
          <div className='w-80'>
            <Input
              id='filter-value'
              placeholder='Search value'
              value={localValue}
              onChange={e => setLocalValue(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && localValue !== filters.value) {
                  onFiltersChange({
                    ...filters,
                    value: localValue || undefined,
                  })
                }
              }}
              className='h-8 text-xs'
            />
          </div>
        </div>

        {/* Right Side Filters */}
        <div className='flex gap-3 items-center'>
          {/* Status Filter */}
          <div className='w-32'>
            <Select
              value={filters.status || 'all'}
              onValueChange={value =>
                updateFilter('status', value === 'all' ? undefined : value)
              }
            >
              <SelectTrigger className='h-8 text-xs'>
                <SelectValue placeholder='Status' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Status</SelectItem>
                <SelectItem value={CUSTOMER_STATUS.active}>Active</SelectItem>
                <SelectItem value={CUSTOMER_STATUS.inactive}>
                  Inactive
                </SelectItem>
                <SelectItem value={CUSTOMER_STATUS.suspended}>
                  Suspended
                </SelectItem>
                <SelectItem value={CUSTOMER_STATUS.archived}>
                  Archived
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reset Button - Only show when there are active filters */}
          {hasActiveFilters && (
            <Button
              variant='outline'
              size='sm'
              onClick={onReset}
              className='h-8'
            >
              Reset
            </Button>
          )}
        </div>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className='flex gap-2 flex-wrap mt-3'>
          <span className='text-sm text-muted-foreground'>Active filters:</span>

          {filters.field && filters.operator && filters.value && (
            <Badge variant='secondary' className='gap-1'>
              {filters.field} {filters.operator} "{filters.value}"
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => {
                  clearFilter('field')
                  clearFilter('operator')
                  clearFilter('value')
                }}
              />
            </Badge>
          )}

          {filters.status && (
            <Badge variant='secondary' className='gap-1'>
              Status: {filters.status}
              <X
                className='w-3 h-3 cursor-pointer'
                onClick={() => clearFilter('status')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  )
}
